import { scopedThreadKey, scopeThreadRef } from "@cmmd-forge/client-runtime";
import type { EnvironmentId, ProjectId, ScopedProjectRef, ThreadId } from "@cmmd-forge/contracts";
import { isSidebarThreadVisible } from "./threadVisibility";
import { matchesStatusFilter, type SidebarStatusFilter } from "./sidebarViewFilters";

export interface ProjectThreadInput {
  readonly archivedAt: string | null;
  readonly environmentId: EnvironmentId;
  readonly id: ThreadId;
}

export interface ProjectOwnedThreadInput extends ProjectThreadInput {
  readonly projectId: ProjectId;
}

export interface ProjectHierarchyThreadInput extends ProjectThreadInput {
  readonly kind?: "master" | "worker" | null;
}

function projectRefKey(environmentId: EnvironmentId, projectId: ProjectId): string {
  return `${environmentId}\0${projectId}`;
}

export function selectProjectThreadsFromSource<T extends ProjectOwnedThreadInput>(input: {
  readonly threads: readonly T[];
  readonly projectRef: ScopedProjectRef;
  readonly memberProjectRefs: readonly ScopedProjectRef[];
}): readonly T[] {
  const projectRefKeys = new Set(
    [input.projectRef, ...input.memberProjectRefs].map((ref) =>
      projectRefKey(ref.environmentId, ref.projectId),
    ),
  );
  return input.threads.filter((thread) =>
    projectRefKeys.has(projectRefKey(thread.environmentId, thread.projectId)),
  );
}

/**
 * Picks the threads a project row should show in the sidebar:
 *  1. drop archived rows (archive is recoverable, so the projection stays in
 *     the store — the sidebar just hides it)
 *  2. drop anything the caller explicitly excluded (e.g. workers rendered
 *     elsewhere in the tree)
 */
/**
 * `statusFilter` defaults to "active" so every existing caller keeps the
 * historical behaviour of hiding archived threads. Only the sidebar's workspace
 * list options menu passes anything else.
 */
export function computeVisibleProjectThreads<T extends ProjectThreadInput>(
  allThreads: readonly T[],
  excludedThreadKeys: ReadonlySet<string> | undefined,
  statusFilter: SidebarStatusFilter = "active",
): readonly T[] {
  const active = allThreads.filter((thread) =>
    statusFilter === "active"
      ? isSidebarThreadVisible(thread)
      : matchesStatusFilter(thread, statusFilter),
  );
  if (!excludedThreadKeys || excludedThreadKeys.size === 0) return active;
  return active.filter(
    (thread) =>
      !excludedThreadKeys.has(scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id))),
  );
}

/** Workers are rendered only beneath their canonical master row. */
export function selectTopLevelProjectThreads<T extends ProjectHierarchyThreadInput>(
  threads: readonly T[],
): readonly T[] {
  return threads.filter((thread) => thread.kind !== "worker");
}
